import { groupProspectRows } from "./prospectGroups";
import { Loader2 } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
const base = "commercial/france-travail";
const splitCodes = (value) =>
  value
    .split(/[,;\s]+/)
    .map((v) => v.trim().toUpperCase())
    .filter(Boolean);
const field = "w-full rounded-lg border border-slate-300 bg-white p-2 text-sm";
const button =
  "rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold disabled:opacity-50";
const nextButton = "primary rounded-xl bg-blue-800 px-5 py-3 font-semibold text-white disabled:opacity-50";
const activeStates = ["En attente de traitement", "En cours"];
const template =
  "reference;url;title;company;siret;address;city;postCode;commune;domain;contract;duration;publishedAt;description;quantity\n";
export default function FranceTravailRobot({ api, onSync, inlineAmbiguities = false }) {
  const [step, setStep] = useState(inlineAmbiguities ? 4 : 1);
  const [pendingMode, setPendingMode] = useState(inlineAmbiguities);
  const [pendingRows, setPendingRows] = useState([]);
  const loadPending = useCallback(async () => {
    const { data } = await api.get(`${base}/ambiguous`);
    setPendingRows(data);
  }, [api]);

  const [open, setOpen] = useState(inlineAmbiguities),
    [meta, setMeta] = useState(null),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false);
  const [agency, setAgency] = useState(""),
    [owner, setOwner] = useState(""),
    [domains, setDomains] = useState([]);
  const [departments, setDepartments] = useState(""),
    [communes, setCommunes] = useState(""),
    [radius, setRadius] = useState(10);
  const [date, setDate] = useState(""),
    [contract, setContract] = useState(""),
    [partners, setPartners] = useState(true);
  const [csv, setCsv] = useState(""),
    [fileName, setFileName] = useState("");
  const [collection, setCollection] = useState(null);
  const [collections, setCollections] = useState([]);
  const [job, setJob] = useState(null),
    [history, setHistory] = useState([]),
    [selected, setSelected] = useState([]);
  const [manual, setManual] = useState({
    reference: "",
    title: "",
    company: "",
    siret: "",
    city: "",
    postCode: "",
    address: "",
    contract: "",
  });
  const [editing, setEditing] = useState(null),
    [notice, setNotice] = useState("");
  const sync = useRef(onSync);
  sync.current = onSync;
  const [sources, setSources] = useState(["France Travail"]);
  const filters = {
    sources,
    domains,
    departments: splitCodes(departments),
    communes: splitCodes(communes),
    radius: Number(radius),
    publishedAfter: date,
    contract,
    partners,
  };
  const report = useCallback((err) => {
    const message =
      err?.response?.data?.message || err.message || "Opération indisponible.";
    setError(Array.isArray(message) ? message.join(" · ") : message);
  }, []);
  useEffect(() => {
    if (!open || !pendingMode) return;
    loadPending().catch(report);
    const timer = setInterval(() => { loadPending().catch(report); }, 3000);
    return () => clearInterval(timer);
  }, [open, pendingMode, loadPending, report]);
  const refresh = useCallback(async () => {
    const [needs, imports] = await Promise.all([
      api.get(`${base}/needs`),
      api.get(`${base}/jobs`),
    ]);
    sync.current?.(needs.data);
    setHistory(imports.data);
  }, [api]);
  useEffect(() => {
    let active = true;
    Promise.all([
      api.get(`${base}/metadata`),
      api.get(`${base}/collections`),
      api.get(`${base}/needs`),
      api.get(`${base}/jobs`),
    ])
      .then(([config, collected, needs, imports]) => {
        setCollections(collected.data);
        const latest = collected.data.find(c => activeStates.includes(c.status) || c.status === "Suspendu" || (c.autoImport && ["En attente", "En cours"].includes(c.autoImportStatus)));
        if (latest) setCollection(latest);
        if (!active) return;
        setMeta(config.data);
        setAgency(config.data.currentAgency);
        setOwner(config.data.currentUser);
        setHistory(imports.data);
        sync.current?.(needs.data);
      })
      .catch((err) => {
        if (active) report(err);
      });
    return () => {
      active = false;
    };
  }, [api, report]);
  useEffect(() => {
    if (!job?._id || !activeStates.includes(job.status)) return undefined;
    let active = true,
      pending = false;
    const timer = setInterval(async () => {
      if (pending) return;
      pending = true;
      try {
        const { data } = await api.get(`${base}/jobs/${job._id}`);
        if (!active) return;
        setJob(data);
        if (!activeStates.includes(data.status)) await refresh();
      } catch (err) {
        if (active) report(err);
      } finally {
        pending = false;
      }
    }, 1500);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [api, job?._id, job?.status, refresh, report]); // The queue continues server-side when this window is closed.
  useEffect(() => {
    if (!collection?._id || (!activeStates.includes(collection.status) && !(collection.autoImport && ["En attente", "En cours"].includes(collection.autoImportStatus) && (collection.count || collection.offers?.length || 0) > 0 && !["Suspendu", "Erreur"].includes(collection.status)))) return undefined;
    let active = true;
    const timer = setInterval(async () => {
      try {
        const { data } = await api.get(`${base}/collections/${collection._id}`);
        if (active) {
          setCollection(data); setCollections(current => current.map(item => item._id === data._id ? data : item));
          if (data.previewId) {
            const result = await api.get(`${base}/jobs/${data.previewId}`);
            if (active) {setJob(result.data); await refresh();}
          }
        }
      } catch (err) { if (active) report(err); }
    }, 1500);
    return () => { active = false; clearInterval(timer); };
  }, [api, collection?._id, collection?.status, collection?.autoImportStatus, collection?.autoImport, report, refresh]);
  const collectionAction = (action) => operation(async () => {
    const { data } = await api.post(`${base}/collections/${collection._id}/${action}`);
    if (action === "preview") {
      setStep(4);
      setJob(data);
      setSelected(data.rows.filter(r => ["create", "enrich"].includes(r.match?.state)).map(r => r._id));
      const updated = await api.get(`${base}/collections/${collection._id}`);
      setCollection(updated.data);
      await refresh();
    } else setCollection(data);
  });
  const operation = async (fn) => {
    if (busy) return;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await fn();
    } catch (err) {
      report(err);
    } finally {
      setBusy(false);
    }
  };
  const preview = () =>
    operation(async () => {
      const { data } = await api.post(`${base}/preview`, {
        agency,
        ownerId: owner,
        filters,
        ...(csv ? { csv } : { offers: [manual] }),
      });
      setJob(data);
      setStep(4);
      setSelected(
        data.rows
          .filter((r) => ["create", "enrich"].includes(r.match?.state))
          .map((r) => r._id),
      );
      await refresh();
    });
  const inspect = (id) =>
    operation(async () => {
      const { data } = await api.get(`${base}/jobs/${id}`);
      setJob(data);
      setStep(4);
      setSelected(
        data.rows
          .filter(
            (r) =>
              ["pending", "error"].includes(r.state) &&
              ["create", "enrich"].includes(r.match?.state),
          )
          .map((r) => r._id),
      );
    });
  const run = () =>
    operation(async () => {
      const { data } = await api.post(`${base}/jobs/${job._id}/run`, {
        selected: selected.filter(id=>job.rows.some(row=>row._id===id && ["pending","error"].includes(row.state) && ["create","enrich"].includes(row.match?.state))),
      });
      setJob(data);
    });
  const control = (action) =>
    operation(async () => {
      const { data } = await api.post(`${base}/jobs/${job._id}/${action}`);
      setJob(data);
      await refresh();
    });
  const resolve = (row, changes) =>
    operation(async () => {
      const { data } = await api.post(
        `${base}/jobs/${job._id}/rows/${encodeURIComponent(row._id)}`,
        changes,
      );
      setJob(data);
      setStep(4);
      setSelected(
        data.rows
          .filter(
            (r) =>
              r.state === "pending" &&
              ["create", "enrich"].includes(r.match?.state),
          )
          .map((r) => r._id),
      );
      const updated = data.rows.find(item => item._id === row._id);
      const unresolved = updated && !["imported", "ignored"].includes(updated.state) && !["create", "enrich"].includes(updated.match?.state);
      if (unresolved) {
        setEditing(current => ({ ...row, ...current, ...updated,
          draft: Object.fromEntries(["company", "siret", "address", "city", "postCode"].map(key => [key, updated.offer?.[key] || ""])), clientId: changes.clientId || "",
          applyToRelated: changes.applyToRelated === true,
        }));
        setNotice("Correction enregistrée. Le dossier reste à vérifier avant création du prospect.");
      } else {
        setEditing(null);
        if (pendingMode && changes.ignore !== true) {
          const references = data.correction?.references || [row.reference];
          const validated = data.rows.filter(item => references.includes(item.reference) && ["pending","error"].includes(item.state) && ["create","enrich"].includes(item.match?.state)).map(item=>item._id);
          if(validated.length){const result = await api.post(`${base}/jobs/${job._id}/run`, {selected:validated});setJob(result.data);setNotice("Validation enregistrée. Création ou mise à jour de la fiche en cours.");}
          await loadPending();
        }
        if (pendingMode && changes.ignore === true) await loadPending();
        if(data.correction && !pendingMode) setNotice(`${data.correction.company} : ${data.correction.references.length} annonce(s) mise(s) à jour.`);
      }
    });
  const downloadTemplate = () => {
    const url = URL.createObjectURL(
      new Blob(["\uFEFF" + template], { type: "text/csv;charset=utf-8" }),
    );
    const a = document.createElement("a");
    a.href = url;
    a.download = "modele-import-france-travail.csv";
    a.click();
    URL.revokeObjectURL(url);
  };
  const owners = (meta?.owners || []).filter(
    (user) => String(user.agency?._id || user.agency) === agency,
  );
  const running = job && activeStates.includes(job.status);
  const searching = activeStates.includes(collection?.status);
  const updating = (collection?.count || collection?.offers?.length || 0) > 0 && collection?.autoImport && ["En attente", "En cours"].includes(collection.autoImportStatus) && !["Suspendu", "Erreur"].includes(collection.status);
  const working = busy || searching || running || updating;
  const clientGroups=groupProspectRows(job?.rows || []);
  const ambiguousRows = (job?.rows || []).filter(row=>!["imported","ignored"].includes(row.state) && !["create","enrich"].includes(row.match?.state));
  const readySelected = selected.filter(id=>(job?.rows || []).some(row=>row._id===id && ["pending","error"].includes(row.state) && ["create","enrich"].includes(row.match?.state)));
  const validZone = (filters.departments.length > 0 || filters.communes.length > 0) && filters.departments.every(code => /^(\d{2}|2A|2B|97\d)$/.test(code)) && filters.communes.every(code => /^[0-9AB]{5}$/.test(code)) && Boolean(owner);

  return (
    <>
      {!inlineAmbiguities && <button type="button" onClick={() => {setPendingMode(false);setEditing(null);setStep(working ? 3 : job ? 4 : collection ? 3 : 1);setOpen(true);}} className={`secondary ${button}`}>
        Générer de nouveaux leads
      </button>}
      {open && (
        <div className={inlineAmbiguities ? "mt-3" : "fixed inset-0 z-[1000] overflow-y-auto bg-black/50 p-3 sm:p-6"}>
          <section
            role={inlineAmbiguities ? "region" : "dialog"}
            aria-modal={inlineAmbiguities ? undefined : true}
            aria-labelledby="ft-robot-title"
            className={inlineAmbiguities ? "space-y-4 text-slate-900" : "mx-auto max-w-4xl space-y-5 rounded-2xl bg-white p-5 text-slate-900 shadow-xl"}
          >
            <header className="flex justify-between gap-4">
              <div>
                <h2 id="ft-robot-title" className="text-xl font-bold">
                  {pendingMode ? "Ambiguës à vérifier" : "Générer de nouveaux leads"}
                </h2>
                <p className="mt-1 text-sm">
                  {pendingMode ? "Ces annonces sont conservées sans fiche client. Vérifiez-les puis validez pour créer ou compléter la fiche." : "Choisissez votre zone et vos secteurs, vérifiez les résultats, puis créez vos prospects."}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                hidden={inlineAmbiguities}
                className={button}
              >
                Fermer
              </button>
            </header>
            {error && (
              <p
                role="alert"
                className="rounded-lg bg-rose-50 p-3 text-rose-800"
              >
                {error}
              </p>
            )}
            {notice && (
              <p
                role="status"
                className="rounded-lg bg-emerald-50 p-3 text-emerald-800"
              >
                {notice}
              </p>
            )}
            {!meta ? (
              <p role="status" className="flex items-center gap-2"><Loader2 aria-hidden="true" className="animate-spin" size={20}/>Chargement…</p>
            ) : (
              <>
                <ol style={{display:pendingMode ? "none" : undefined}} aria-label="Étapes de génération de leads" className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
                  {["Zone", "Secteur", "Recherche", "Traiter les ambiguës", "Résultats", "Créer les prospects"].map((label,index)=><li key={label}><button type="button" aria-current={step===index+1 ? "step" : undefined} disabled={working || index+1>step} onClick={()=>{setEditing(null);setStep(index+1);}} className={`flex h-full w-full items-center gap-2 rounded-xl border p-3 text-sm font-semibold disabled:opacity-50 ${step===index+1 ? "border-blue-700 bg-blue-50 text-blue-800" : "border-slate-200 bg-white text-slate-500"}`}><span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${step===index+1 ? "bg-blue-800 text-white" : "bg-slate-100"}`}>{index+1}</span>{label}</button></li>)}
                </ol>
                <div style={{display:step===1 ? "block" : "none"}} className="space-y-4">
                <section className="space-y-3 rounded-xl border border-slate-200 p-4">
                  <h3 className="font-semibold">1. Où souhaitez-vous prospecter ?</h3>
                  <label>
                    Départements à prospecter
                    <input
                      className={field}
                      value={departments}
                      onChange={(e) => setDepartments(e.target.value)}
                      placeholder="Ex. 92, 75, 31"
                    />
                  </label>
                  <p className="text-sm text-slate-500">Saisissez un ou plusieurs numéros de département : 92 pour les Hauts-de-Seine, 75 pour Paris, 31 pour la Haute-Garonne.</p>
                <section className="border-t border-slate-200 pt-3"><h4 className="font-semibold">Options de recherche</h4><div className="mt-4 grid gap-3 sm:grid-cols-2">
                  <label>
                    Communes (codes INSEE)
                    <input
                      className={field}
                      value={communes}
                      onChange={(e) => setCommunes(e.target.value)}
                      placeholder="94041, 31555…"
                    />
                  </label>                  <label>
                    Rayon (km)
                    <input
                      className={field}
                      type="number"
                      min="0"
                      max="100"
                      value={radius}
                      onChange={(e) => setRadius(e.target.value)}
                    />
                  </label>                  <label>
                    Publication depuis
                    <input lang="fr-FR"
                      className={field}
                      type="date"
                      value={date}
                      onChange={(e) => setDate(e.target.value)}
                    />
                  </label>                  <label>
                    Type de contrat
                    <select
                      className={field}
                      value={contract}
                      onChange={(e) => setContract(e.target.value)}
                    >
                      <option value="">Tous</option>
                      {["CDI", "CDD", "Intérim"].map(
                        (t) => (
                          <option key={t}>{t}</option>
                        ),
                      )}
                    </select>
                  </label>                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={partners}
                      onChange={(e) => setPartners(e.target.checked)}
                    />
                    Inclure les offres partenaires
                  </label>
                </div></section>
                </section>
                {meta.admin && <details className="rounded-xl border border-slate-200 p-4"><summary className="cursor-pointer font-semibold">Agence et responsable des nouveaux leads</summary><div className="mt-4 grid gap-3 sm:grid-cols-2">                  <label>
                    Agence commerciale
                    <select
                      className={field}
                      value={agency}
                      onChange={(e) => {
                        setAgency(e.target.value);
                        setOwner("");
                      }}
                    >
                      {meta.agencies.map((a) => (
                        <option key={a._id} value={a._id}>
                          {a.name}
                        </option>
                      ))}
                    </select>
                  </label>                  <label>
                    Commercial responsable
                    <select
                      className={field}
                      value={owner}
                      onChange={(e) => setOwner(e.target.value)}
                    >
                      <option value="">Sélectionner</option>
                      {owners.map((o) => (
                        <option key={o._id} value={o._id}>
                          {[o.firstname, o.lastname]
                            .filter(Boolean)
                            .join(" ") || o._id}
                        </option>
                      ))}
                    </select>
                  </label></div></details>}
                {meta.admin && (
                  <details className="rounded-lg border p-3">
                    <summary>Zones de prospection ADMIN</summary>
                    <p className="my-2 text-sm">
                      Enregistrez les départements et communes saisis ci-dessus
                      pour l’agence et le commercial sélectionnés. Une zone
                      ambiguë reste à vérifier. Les commerciaux des clients
                      existants sont conservés.
                    </p>
                    <p className="text-sm">
                      Zones actuelles :{" "}
                      {meta.zones
                        .map(
                          (z) =>
                            `${meta.agencies.find((a) => String(a._id) === z.agency)?.name || z.agency} : ${[...(z.departments || []), ...(z.communes || [])].join(", ")}`,
                        )
                        .join(" · ") ||
                        "Aucune — affectation manuelle à l’agence et au commercial sélectionnés."}
                    </p>
                    <button
                      type="button"
                      className={button}
                      disabled={busy || !owner}
                      onClick={() =>
                        operation(async () => {
                          const { data } = await api.post(`${base}/zones`, {
                            agency,
                            ownerId: owner,
                            departments: splitCodes(departments),
                            communes: splitCodes(communes),
                          });
                          setMeta(data);
                          setNotice("Zone enregistrée.");
                        })
                      }
                    >
                      Enregistrer la zone de cette agence
                    </button>
                  </details>
                )}
                {!validZone && <p className="text-sm text-slate-500">Choisissez une zone de recherche et un responsable pour continuer.</p>}
                <div className="flex justify-end"><button type="button" className={nextButton} disabled={!validZone || working} onClick={()=>setStep(2)}>Suivant</button></div>

                </div>
                <div style={{display:step===2 ? "block" : "none"}} className="space-y-4">
                  <p className="text-sm text-slate-500">Zone choisie : {[...filters.departments,...filters.communes].join(", ")}</p>
                <fieldset className="rounded-lg border p-4">
                  <legend className="font-semibold">2. Quels secteurs vous intéressent ?</legend>
                  <fieldset className="mb-4 rounded-lg border bg-white p-3"><legend className="font-semibold">Sites à consulter</legend>
                    <div className="flex flex-wrap gap-4">{["France Travail","Staffsanté","Staffsocial","FHF Emploi"].map(source=><label key={source} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={sources.includes(source)} disabled={working} onChange={event=>setSources(current=>event.target.checked?[...current,source]:current.filter(value=>value!==source))}/>{source}</label>)}</div>
                    <p className="mt-2 text-sm text-slate-500">Les trois sites santé nécessitent un département. Les annonces sont vérifiées avant import.</p>
                  </fieldset>

                  <details open><summary className="cursor-pointer py-2 text-sm font-medium">{domains.length ? `${domains.length} secteur${domains.length > 1 ? "s" : ""} sélectionné${domains.length > 1 ? "s" : ""}` : "Tous les secteurs — modifier"}</summary>
                  <p className="mb-3 text-sm text-slate-500">Sans sélection, la recherche porte sur tous les secteurs.</p>
                  <div className="mb-3 flex gap-2">
                    <button
                      type="button"
                      className={button}
                      onClick={() =>
                        setDomains(meta.domains.map((d) => d.label))
                      }
                    >
                      Sélectionner tous les domaines
                    </button>
                    <button
                      type="button"
                      className={button}
                      onClick={() => setDomains([])}
                    >
                      Effacer la sélection
                    </button>
                  </div>
                  <div className="grid gap-2 sm:grid-cols-3">
                    {meta.domains.map((d) => (
                      <label key={d.label} className="flex gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={domains.includes(d.label)}
                          onChange={(e) =>
                            setDomains((current) =>
                              e.target.checked
                                ? [...current, d.label]
                                : current.filter((v) => v !== d.label),
                            )
                          }
                        />
                        {d.label}
                      </label>
                    ))}
                  </div>
                  </details>
                </fieldset>
                <p className="text-sm text-slate-500">Les nouveaux prospects seront rattachés à {meta.agencies.find(a => String(a._id) === agency)?.name || "votre agence"}. Les doublons sont rapprochés automatiquement.</p>
                <div className="flex items-center justify-between gap-3" style={{display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12}}>
                  <button type="button" className={button} disabled={working} onClick={()=>setStep(1)}>Précédent</button>
                <button type="button" style={{width: "auto"}} className={nextButton}
                  disabled={working || !validZone || !sources.length || (sources.some(source=>source!=="France Travail") && !filters.departments.length)}
                  onClick={() => operation(async () => {
                    setJob(null); setCollection(null); setStep(3);
                    const { data } = await api.post(`${base}/browser`, {agency, ownerId: owner, filters});
                    setJob(null); setCollection(data); setCollections(current => [data, ...current]);
                  })}>
                  {working ? "Traitement en cours…" : "Suivant"}
                </button>
                </div>
                </div>
                <div style={{display:step===3 ? "block" : "none"}} className="space-y-4" aria-busy={Boolean(working)}>
                  {working && <div role="status" aria-live="polite" className="flex items-center gap-4 rounded-xl bg-blue-50 p-5 text-blue-900"><Loader2 aria-hidden="true" className="animate-spin motion-reduce:animate-none" style={{width:28,height:28}} size={28}/><div><strong>{searching ? "Recherche des entreprises…" : busy && !collection ? "Démarrage de la recherche…" : "Mise à jour des fiches et des actions…"}</strong>{searching && collection?.progress ? <div className="mt-2 space-y-1 text-sm"><p className="font-semibold">{collection.progress.source} · {collection.progress.stage}</p><p>Zone {collection.progress.zone}/{collection.progress.zones} · Lot {collection.progress.batch}</p><p>{collection.scanned || 0} annonces examinées · {collection.count || 0} offres retenues</p><a className="block break-all underline" href={collection.progress.url} target="_blank" rel="noreferrer">{collection.progress.url}</a></div> : <p className="mt-1 text-sm">{searching ? "En attente du prochain état du navigateur…" : "Préparation du traitement…"}</p>}</div></div>}
                {collection && (
                  <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm" role="status">
                    <strong>Recherche : {collection.status}</strong>
                    <p>{collection.count || collection.offers?.length || 0} offres retenues · {collection.scanned || 0} annonces consultées</p>
                    {collection.sourceStats && <div className="space-y-2">{Object.entries(collection.sourceStats).map(([source,stats])=><div key={source} className="rounded-lg border bg-white p-3 text-sm"><strong>{source} · {stats.status}</strong><p>{stats.scanned}{stats.total ? ` / ${stats.total}` : ""} annonces examinées · {stats.retained} offres retenues{stats.issues ? ` · ${stats.issues} annonces à vérifier non retenues` : ''}</p>{stats.reason && <p>{stats.reason}</p>}</div>)}</div>}
                    {collection.reason && <p>{collection.reason}</p>}{collection.autoImportError && <p role="alert" className="text-red-700">{collection.autoImportError}</p>}{collection.autoImport && <p>Mise à jour des fiches : {collection.autoImportStatus === "Préparé" ? "Résultats disponibles ci-dessous" : collection.autoImportStatus}</p>}
                    <p>La recherche ne crée aucune fiche. Vous vérifierez les correspondances avant de créer les prospects à l’étape 6.</p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {activeStates.includes(collection.status) && <button type="button" className={button} disabled={busy} onClick={() => collectionAction("pause")}>Arrêter la recherche</button>}
                      {!collection.previewId && ["Suspendu", "Erreur", "Terminé partiellement"].includes(collection.status) && (collection.count || 0) < (meta.limits?.rows || 5000) && <button type="button" className={button} disabled={busy} onClick={() => collectionAction("resume")}>Reprendre la recherche</button>}

                    </div>
                  </div>
                )}

                  {!working && !job && collection && !(collection.count || collection.offers?.length) && <p>Aucune offre trouvée. Revenez aux critères pour élargir la recherche.</p>}
                  <div className="flex items-center justify-between gap-3" style={{display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12}}>
                    <button type="button" className={button} disabled={working} onClick={()=>setStep(2)}>Précédent</button>
                    {job ? <button type="button" className={nextButton} disabled={working} onClick={()=>setStep(4)}>Suivant</button> : collection && !activeStates.includes(collection.status) && (collection.count || collection.offers?.length || 0) > 0 ? <button type="button" className={nextButton} disabled={busy} onClick={() => collectionAction("preview")}>Suivant</button> : null}
                  </div>
                </div>
                <div style={{display:step>=4 ? "block" : "none"}} className="space-y-4" aria-busy={Boolean(busy || running)}>
                {pendingMode && <section className="space-y-3" aria-label="Annonces ambiguës en attente">
                  {!pendingRows.length && <p>Aucune annonce ambiguë en attente.</p>}
                  {pendingRows.map(row => <article key={row._id} className={inlineAmbiguities ? "ambiguityCard" : "rounded-xl border border-slate-200 p-3"}>
                    <strong>{row.offer.company || "Entreprise à identifier"}</strong><p>{row.offer.title} · {row.offer.postCode} {row.offer.city}</p>
                    <a href={row.offer.url} target="_blank" rel="noopener noreferrer" className="block text-sm text-blue-700 underline">Voir l’annonce ↗</a>
                    <p className="text-sm">{row.match?.label}{row.error ? ` · ${row.error}` : ""}</p>
                    <button type="button" className={button} disabled={busy || running} onClick={() => operation(async () => {
                      const {data} = await api.get(`${base}/jobs/${row.jobId}`);setJob(data);
                      const current = data.rows.find(item => item._id === row._id);
                      if(!current || ["imported","ignored"].includes(current.state)){await loadPending();return;}
                      setEditing({...current,applyToRelated:true,draft:Object.fromEntries(["company","siret","address","city","postCode"].map(key=>[key,current.offer[key] || ""]))});
                    })}>Modifier nom client</button>
                    <button type="button" className={button} disabled={busy || running} onClick={() => operation(async () => {
                      const {data} = await api.post(`${base}/jobs/${row.jobId}/rows/${encodeURIComponent(row._id)}`, {ignore:true});
                      setJob(data);setEditing(null);await loadPending();await refresh();
                    })}>Ignorer</button>
                  </article>)}
                </section>}
                {editing && (
                  <section className="space-y-3 rounded-lg border border-blue-300 bg-blue-50 p-4">
                    <h3 className="font-semibold">
                      Vérifier {editing.reference}
                    </h3>
                    <p className="text-sm">
                      Renseignez uniquement des informations vérifiées. Le
                      rapprochement sera contrôlé à nouveau côté serveur.
                    </p>
                    <div className={inlineAmbiguities ? "grid gap-3" : "grid gap-3 sm:grid-cols-3"}>
                      {Object.keys(editing.draft).map((key) => (
                        <label key={key}>
                          {
                            {
                              company: "Nom du client",
                              siret: "SIRET",
                              address: "Adresse",
                              city: "Ville",
                              postCode: "Code postal",
                            }[key]
                          }
                          <input
                            className={field}
                            autoFocus={key === "company"}
                            value={editing.draft[key]}
                            onChange={(e) =>
                              setEditing((v) => ({
                                ...v,
                                draft: { ...v.draft, [key]: e.target.value },
                              }))
                            }
                          />
                        </label>
                      ))}
                    </div>
                    <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!editing.applyToRelated} onChange={event=>setEditing(current=>({...current,applyToRelated:event.target.checked}))}/>Appliquer cette correction aux autres annonces du même établissement</label>
                    {editing.match?.candidates?.length > 0 && (
                      <label>
                        Fiche existante
                        <select
                          className={field}
                          value={editing.clientId || ""}
                          onChange={(e) =>
                            setEditing((v) => ({
                              ...v,
                              clientId: e.target.value,
                            }))
                          }
                        >
                          <option value="">Choisir une fiche existante ou compléter les coordonnées</option>
                          {editing.match.candidates.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.name} · {c.siret} · {c.address} {c.postCode}
                            </option>
                          ))}
                        </select>
                      </label>
                    )}
                    <p role="status" className="text-sm text-blue-900">{editing.match?.label}. {editing.match?.candidates?.length ? "Choisissez ci-dessus la fiche correspondant à cette entreprise pour éviter un doublon." : "Complétez le nom et les coordonnées vérifiées, puis enregistrez."}</p>
                    <button
                      type="button"
                      className={button}
                      disabled={busy}
                      onClick={() =>
                        resolve(editing, {
                          ...editing.draft,
                          clientId: editing.clientId || "",
                          applyToRelated: editing.applyToRelated === true,
                        })
                      }
                    >
                      {busy ? "Enregistrement…" : pendingMode ? "Valider et créer la fiche" : "Enregistrer la correction"}
                    </button>
                    <button
                      type="button"
                      className={button}
                      onClick={() => setEditing(null)}
                    >
                      Annuler
                    </button>
                  </section>
                )}
                {job && !pendingMode && (
                  <section className="space-y-3 rounded-xl border border-slate-200 p-4">
                    <h3 className="text-lg font-semibold">{step===4 ? "4. Traiter les correspondances ambiguës" : step===5 ? "5. Résultats" : "6. Créer les prospects"}</h3>
                    {step===4 && <p className="text-sm">Les annonces non traitées restent dans « Ambiguës à vérifier » dans Actions commerciales. Vous pouvez continuer et importer les leads vérifiés.</p>}
                    {step===4 && !job.rows.some(r=>!["imported","ignored"].includes(r.state) && !["create","enrich"].includes(r.match?.state)) && <p role="status">Aucune correspondance ambiguë à traiter.</p>}
                    {step===6 && running && <p role="status" className="flex items-center gap-2"><Loader2 aria-hidden="true" className="animate-spin" size={22}/>Création des prospects et mise à jour des fiches…</p>}
                    {step===6 && !running && <p className="text-sm">Seules les lignes cochées et vérifiées seront créées ou enrichies. Les contrôles anti-doublon restent actifs.</p>}
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">{[[clientGroups.filter(rows=>rows.some(row=>row.match?.state==="create")).length,"Nouveaux prospects"],[clientGroups.filter(rows=>rows.some(row=>row.match?.state==="enrich") && !rows.some(row=>row.match?.state==="create")).length,"Clients existants"],[job.stats.duplicates || 0,"Annonces déjà connues"],[job.rows.filter(r=>!["imported","ignored"].includes(r.state) && !["create","enrich"].includes(r.match?.state)).length,"À vérifier"]].map(([value,label])=><div key={label} className="rounded-xl bg-slate-50 p-3"><strong className="text-2xl">{value}</strong><p className="text-sm text-slate-600">{label}</p></div>)}</div>
                    <p className="text-sm text-slate-600">{running ? "Vos fiches et actions commerciales sont en cours de mise à jour…" : job.rows.some(r=>r.state === "imported") ? "Les fiches traitées sont disponibles dans vos clients et actions commerciales." : "Consultez les entreprises ci-dessous avant de passer à la création."}</p>
                    {step>=5 && <section aria-label="Annonces regroupées par client" className="space-y-3"><h4 className="font-semibold">Une fiche client, toutes ses annonces</h4>{clientGroups.map(rows=><article key={rows[0]._id} className="rounded-lg border border-slate-200 p-3"><strong>{rows[0].offer.company || "Client à identifier"}</strong><p className="text-sm">{rows.length} annonce{rows.length>1?"s":""} · {rows[0].offer.city}</p><ul className="mt-2 space-y-1 text-sm">{rows.map(row=><li key={row._id}><a href={row.offer.url} target="_blank" rel="noreferrer" className="underline">{row.offer.title} · {row.reference}</a>{row.offer.contract?` · ${row.offer.contract}`:""}</li>)}</ul></article>)}</section>}
                    <details open><summary className="cursor-pointer py-2 font-semibold">{step===4 ? "Correspondances à vérifier" : step===5 ? "Entreprises sélectionnées" : "Détail de la création"}</summary><div className="mt-3 space-y-3">
                    <div style={{display:step===6 ? "block" : "none"}}>
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <h3 className="font-semibold">
                        Import du{" "}
                        {new Date(job.createdAt).toLocaleString("fr-FR", {
                          timeZone: "Europe/Paris",
                        })}{" "}
                        — {job.status}
                      </h3>
                      <div className="flex flex-wrap gap-2" style={{display:step===6 ? "flex" : "none"}}>
                        <button
                          type="button"
                          className={button}
                          disabled={busy || running || !readySelected.length}
                          onClick={run}
                        >
                          Créer les prospects sélectionnés ({readySelected.length})
                        </button>
                        <button
                          type="button"
                          className={button}
                          disabled={busy || !running}
                          onClick={() => control("pause")}
                        >
                          Arrêter l’import
                        </button>
                        <button
                          type="button"
                          className={button}
                          disabled={
                            busy ||
                            ![
                              "Suspendu",
                              "Erreur",
                              "Terminé partiellement",
                            ].includes(job.status) ||
                            !job.rows.some(
                              (r) =>
                                r.selected &&
                                ["pending", "error"].includes(r.state),
                            )
                          }
                          onClick={() => control("resume")}
                        >
                          Reprendre
                        </button>
                      </div>
                    </div>
                    <p className="text-sm">
                      Domaines : {job.filters.domains.join(", ") || "Tous"} ·
                      Zones :{" "}
                      {[
                        ...job.filters.departments,
                        ...job.filters.communes,
                      ].join(", ") || "Non précisées"}{" "}
                      · Dernier lot : {job.lastBatch}
                    </p>
                    <progress
                      aria-label="Progression de l’import"
                      value={job.stats.progress}
                      max="100"
                      className="w-full"
                    />
                    <p className="text-sm">
                      {job.stats.progress} % · {job.stats.processed}/
                      {job.stats.offers} annonces traitées · {job.stats.batches}{" "}
                      lots · {job.stats.companies} entreprises identifiées ·{" "}
                      {job.stats.created} prospects créés · {job.stats.enriched}{" "}
                      fiches enrichies · {job.stats.duplicates} doublons évités
                      · {job.stats.anonymous} annonces anonymes ·{" "}
                      {job.stats.errors} erreurs
                    </p>
                    {job.error && <p role="alert">{job.error}</p>}
                    </div>
                    <label style={{display:step>=5 ? "flex" : "none"}} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        disabled={busy || running}
                        checked={
                          selected.length > 0 &&
                          selected.length ===
                            job.rows.filter(
                              (r) =>
                                ["pending", "error"].includes(r.state) &&
                                ["create", "enrich"].includes(r.match?.state),
                            ).length
                        }
                        onChange={(e) =>
                          setSelected(
                            e.target.checked
                              ? job.rows
                                  .filter(
                                    (r) =>
                                      ["pending", "error"].includes(r.state) &&
                                      ["create", "enrich"].includes(
                                        r.match?.state,
                                      ),
                                  )
                                  .map((r) => r._id)
                              : [],
                          )
                        }
                      />
                      Validation groupée des correspondances vérifiées
                    </label>
                    <div className="overflow-x-auto">
                      <table style={{minWidth:900}} className="w-full text-left text-sm">
                        <thead>
                          <tr>
                            {[
                              "Sélection",
                              "Entreprise",
                              "Ville",
                              "Métier recherché",
                              "Contrat",
                              "Source",
                              "État CRM",
                              "Action",
                            ].map((h) => (
                              <th key={h} className="p-2">
                                {h}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {job.rows.filter(row=>step!==4 || (!["imported","ignored"].includes(row.state) && !["create","enrich"].includes(row.match?.state))).map((row) => (
                            <tr key={row._id} className="border-t align-top">
                              <td className="p-2">
                                <input
                                  aria-label={`Sélectionner ${row.reference}`}
                                  type="checkbox"
                                  disabled={
                                    busy ||
                                    running ||
                                    !["pending", "error"].includes(row.state) ||
                                    !["create", "enrich"].includes(
                                      row.match?.state,
                                    )
                                  }
                                  checked={selected.includes(row._id)}
                                  onChange={(e) =>
                                    setSelected((s) =>
                                      e.target.checked
                                        ? [...s, row._id]
                                        : s.filter((id) => id !== row._id),
                                    )
                                  }
                                />
                              </td>
                              <td className="p-2">
                                {row.offer.company || "Entreprise à identifier"}
                                <p className="text-xs">{row.offer.siret}</p>
                              </td>
                              <td className="p-2">
                                {row.offer.postCode} {row.offer.city}
                              </td>
                              <td className="p-2">
                                <a
                                  className="underline"
                                  target="_blank"
                                  rel="noreferrer"
                                  href={row.offer.url}
                                >
                                  {row.offer.title}
                                </a>
                                <p>{row.reference}</p>
                              </td>
                              <td className="p-2">
                                {row.offer.contract || "Non précisé"}
                              </td>
                              <td className="p-2">{row.offer.source || "France Travail"}</td>
                              <td className="p-2">
                                {row.state === "imported"
                                  ? "Importé"
                                  : row.state === "ignored"
                                    ? "Ignoré"
                                    : row.match?.label}
                                {row.error && (
                                  <p role="alert" className="text-rose-700">
                                    {row.error}
                                  </p>
                                )}
                              </td>
                              <td className="space-y-2 p-2">
                                {row.state !== "imported" && (
                                  <>
                                    <button
                                      type="button"
                                      className={button}
                                      disabled={busy || running}
                                      onClick={() =>
                                        setEditing({
                                          ...row,
                                          applyToRelated: true,
                                          draft: {
                                            company: row.offer.company,
                                            siret: row.offer.siret,
                                            address: row.offer.address,
                                            city: row.offer.city,
                                            postCode: row.offer.postCode,
                                          },
                                        })
                                      }
                                    >
                                      Modifier le client
                                    </button>
                                    <button
                                      type="button"
                                      className={button}
                                      disabled={busy || running}
                                      onClick={() =>
                                        resolve(row, { ignore: true })
                                      }
                                    >
                                      Ignorer
                                    </button>
                                  </>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    </div></details>
                  </section>
                )}
                  <div style={{display:pendingMode ? "none" : undefined}} className="flex justify-between gap-3 border-t pt-4"><button type="button" className={button} disabled={working} onClick={()=>{setEditing(null);setStep(step-1);}}>Précédent</button>{step<6 ? <button type="button" className={nextButton} disabled={working || !job || Boolean(editing)} onClick={()=>setStep(step+1)}>Suivant</button> : <button type="button" className={button} disabled={working} onClick={()=>setOpen(false)}>Terminer</button>}</div>
                </div>
                <details className="rounded-xl border border-slate-200 p-4">
                  <summary className="cursor-pointer font-semibold">Mes recherches précédentes</summary>
                {collections.length > 0 && <div className="my-3"><h4 className="mb-2 text-sm font-semibold">Recherches</h4>{collections.map(item => <button type="button" key={item._id} className={`${button} m-1`} disabled={busy} onClick={() => operation(async () => { const {data} = await api.get(`${base}/collections/${item._id}`); setCollection(data); setStep(3); })}>{new Date(item.createdAt).toLocaleString("fr-FR")} · {item.status}</button>)}</div>}
                  <h4 className="mt-4 text-sm font-semibold">Résultats enregistrés</h4>
                  {!history.length ? (
                    <p className="text-sm">Aucun import enregistré.</p>
                  ) : (
                    <ul className="divide-y">
                      {history.map((item) => (
                        <li
                          key={item._id}
                          className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm"
                        >
                          <span>
                            {new Date(item.createdAt).toLocaleString("fr-FR", {
                              timeZone: "Europe/Paris",
                            })}{" "}
                            · {item.status} · {item.count} annonces ·{" "}
                            {item.source} · Agence{" "}
                            {meta.agencies.find(
                              (a) => String(a._id) === item.agency,
                            )?.name || item.agency}{" "}
                            · Utilisateur {item.userName || "Utilisateur"} · Lot{" "}
                            {item.lastBatch}
                            {item.summary && (
                              <>
                                {" "}
                                · {item.summary.created} créées ·{" "}
                                {item.summary.enriched} enrichies ·{" "}
                                {item.summary.errors} erreurs
                              </>
                            )}
                          </span>
                          <button
                            type="button"
                            className={button}
                            disabled={busy}
                            onClick={() => inspect(item._id)}
                          >
                            Consulter / reprendre
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </details>
              </>
            )}
          </section>
        </div>
      )}
    </>
  );
}
